import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { FC, useMemo } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import TableSpacerRow from '@app/shared/components/table/TableSpacerRow';
import { useLocale } from '@app/core/hooks/useLocale';
import { getWaterfallCaseYearValue } from '@app/core/store/pwerm2-calculation-slice-selectors';

interface InterimDistributionsSectionProps {
  columnsCount: number;
  hasInterimDistributions?: boolean;
}

export const InterimDistributionsSection: FC<InterimDistributionsSectionProps> = ({
  columnsCount,
  hasInterimDistributions,
}): JSX.Element => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const { l } = useLocale();

  const hasRedemptions = useMemo(
    () =>
      Object.values(results.waterfall.cases).some((caseItem) =>
        Object.values(caseItem.years).some((year) => year.cumulativeInstitutionalRedemptions !== 0)
      ),
    [results.waterfall.cases]
  );

  const hasCashInterest = useMemo(
    () =>
      Object.values(results.waterfall.cases).some((caseItem) =>
        Object.values(caseItem.years).some((year) => year.cumulativeInstitutionalCashInterest !== 0)
      ),
    [results.waterfall.cases]
  );

  const hasDividends = useMemo(
    () =>
      Object.values(results.waterfall.cases).some((caseItem) =>
        Object.values(caseItem.years).some((year) => year.cumulativeInstitutionalDividends !== 0)
      ),
    [results.waterfall.cases]
  );

  const hasInterimCashFlows = useMemo(
    () =>
      Object.values(results.waterfall.cases).some((caseItem) =>
        Object.values(caseItem.years).some(
          (year) => year.cumulativeInstitutionalInterimCashflows !== 0
        )
      ),
    [results.waterfall.cases]
  );

  if (!hasInterimDistributions) {
    return <></>;
  }

  return (
    <>
      <tr data-testid="sponsor-interim-distributions-header-row">
        <th className="table-primary__cell--header-tertiary">{l('_InterimDistributions')}</th>
        <th colSpan={columnsCount - 1} className="table-primary__cell--header-tertiary" />
      </tr>

      {hasRedemptions && (
        <TableSectionValuesPerForecast
          title={l('_CumulativeRedemption')}
          renderSpacer={false}
          renderCell={({ caseItem, forecast }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                (year) => year.cumulativeInstitutionalRedemptions
              )}
            />
          )}
        />
      )}

      {hasCashInterest && (
        <TableSectionValuesPerForecast
          title={l('_CumulativeCashInterest')}
          renderSpacer={false}
          renderCell={({ caseItem, forecast }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                (year) => year.cumulativeInstitutionalCashInterest
              )}
            />
          )}
        />
      )}

      {hasDividends && (
        <TableSectionValuesPerForecast
          title={l('_CumulativeDividends')}
          renderSpacer={false}
          renderCell={({ caseItem, forecast }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                (year) => year.cumulativeInstitutionalDividends
              )}
            />
          )}
        />
      )}

      {hasInterimCashFlows && (
        <TableSectionValuesPerForecast
          title={l('_CumulativeInterimCashFlows')}
          renderSpacer={false}
          additionalTitleClassNames="table-primary__cell--strong"
          additionalCellClassNames="table-primary__cell--strong"
          renderCell={({ caseItem, forecast }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                (year) => year.cumulativeInstitutionalInterimCashflows
              )}
            />
          )}
        />
      )}

      <TableSpacerRow />
    </>
  );
};
